import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type {
  ActivityGroup,
  AuditEvent,
} from '#/shared/api/generated/audit/models'
import { AuditAction } from '#/shared/api/generated/audit/models'
import { useResourceTimeline } from '#/shared/api/generated/audit/theCargoAudit'

import { ACTIVITY_ENTRY_KIND } from './types'
import type {
  ActivityHistoryChange,
  ActivityHistoryEntry,
  ActivityHistoryUser,
} from './types'

const PAGE_SIZE = 20
const MAX_PAGE_SIZE = 200
const SHIPMENT_RESOURCE = 'shipment'

interface UseActivityHistoryOptions {
  enabled?: boolean
}

export interface UseActivityHistoryResult {
  entries: ActivityHistoryEntry[]
  totalCount: number | undefined
  isLoading: boolean
  hasMore: boolean
  isLoadingMore: boolean
  loadMore: () => void
  exportCsv: () => void
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value)
  return JSON.stringify(value)
}

// The schema types the group's user as an open object — pull the fields out
// defensively at the boundary.
function userField(user: ActivityGroup['user'], key: string): string {
  const value = user[key]
  return typeof value === 'string' ? value : ''
}

function buildUser(group: ActivityGroup): ActivityHistoryUser {
  return {
    id: userField(group.user, 'id') || `actor:${group.group_id}`,
    firstName: userField(group.user, 'first_name'),
    lastName: userField(group.user, 'last_name'),
  }
}

function eventChanges(event: AuditEvent): ActivityHistoryChange[] {
  const fields = event.changed_fields ?? []
  const oldData = event.old_data
  const newData = event.new_data
  return fields.map((field) => ({
    id: `${event.audit_id}:${field}`,
    field,
    oldValue: formatValue(oldData[field]),
    newValue: formatValue(newData[field]),
  }))
}

function buildChanges(group: ActivityGroup): ActivityHistoryChange[] {
  return group.events.flatMap(eventChanges)
}

function groupResourceLabel(group: ActivityGroup): string | undefined {
  for (const event of group.events) {
    if (event.resource_label) return event.resource_label
  }
  return undefined
}

function buildEntry(
  group: ActivityGroup,
  labels: { created: string; deleted: string },
): ActivityHistoryEntry {
  const user = buildUser(group)
  if (group.action === AuditAction.update) {
    return {
      id: group.group_id,
      kind: ACTIVITY_ENTRY_KIND.session,
      user,
      occurredAt: group.occurred_at,
      changes: buildChanges(group),
      resourceLabel: groupResourceLabel(group),
    }
  }
  return {
    id: group.group_id,
    kind: ACTIVITY_ENTRY_KIND.event,
    user,
    occurredAt: group.occurred_at,
    label:
      group.action === AuditAction.create ? labels.created : labels.deleted,
    detail: groupResourceLabel(group),
  }
}

export function useActivityHistory(
  shipmentId: string | undefined,
  { enabled = true }: UseActivityHistoryOptions = {},
): UseActivityHistoryResult {
  const { t } = useTranslation()
  const [limit, setLimit] = useState(PAGE_SIZE)
  const armed = enabled && Boolean(shipmentId)

  const query = useResourceTimeline(
    SHIPMENT_RESOURCE,
    shipmentId ?? '',
    { limit },
    { query: { enabled: armed, staleTime: 0 } },
  )

  const groups = query.data?.data.groups

  const entries = useMemo<ActivityHistoryEntry[]>(() => {
    if (!groups) return []
    const labels = {
      created: t('activityHistory.events.created'),
      deleted: t('activityHistory.events.deleted'),
    }
    return groups.map((group) => buildEntry(group, labels))
  }, [groups, t])

  const reachedMax = limit >= MAX_PAGE_SIZE
  const hasMore = Boolean(query.data?.data.next_before) && !reachedMax

  return {
    entries,
    totalCount: undefined,
    isLoading: armed && query.isPending,
    hasMore,
    isLoadingMore: query.isFetching && !query.isPending,
    loadMore: () => setLimit((n) => Math.min(n + PAGE_SIZE, MAX_PAGE_SIZE)),
    exportCsv: () => {},
  }
}
